/**
 * Responsibility: Model-selection helpers for AI settings and runtime routing.
 * Scope: Owns model catalog, persisted routing state, and scoring helpers only.
 */

export type AIProviderId = 'vertex-managed' | 'gemini' | 'openai' | 'anthropic' | 'groq' | 'ollama' | 'mock' | 'stable-diffusion'
export type AIJobSlot = 'quality' | 'prose' | 'coding' | 'images' | 'music' | 'tts'
export type AISelectionKind = 'provider' | 'job'
export type AISelectionTargetId = `provider:${AIProviderId}` | `job:${AIJobSlot}`

export interface AIModelProfile {
  coding: number
  reasoning: number
  creative: number
  vision: number
  speed: number
  instructions: number
}

export interface AIModelOption {
  ref: string
  providerId: AIProviderId
  providerLabel: string
  model: string
  label: string
  profile: AIModelProfile
  score: number
  costTier: 0 | 1 | 2 | 3
  costLabel: '0' | '+' | '++' | '+++'
  hardwareLabel: string
  hardwareScore: number
  note: string
  available: boolean
  selected: boolean
}

export interface AIModelRoutingState {
  assignments: Record<AISelectionTargetId, string>
}

export interface AIModelAssignmentGroup {
  kind: AISelectionKind
  id: AISelectionTargetId
  label: string
  enabled: boolean
  canToggle: boolean
  keyField?: 'geminiApiKey' | 'openaiApiKey' | 'anthropicApiKey' | 'groqApiKey'
  proxyField?: 'geminiProxyUrl'
  toggleField?: 'geminiEnabled' | 'ollamaEnabled' | 'openaiEnabled' | 'anthropicEnabled' | 'groqEnabled' | 'stableDiffusionEnabled'
  baseUrlField?: 'ollamaBaseUrl' | 'stableDiffusionBaseUrl'
  hardwareField?: 'ollamaHardware'
  desc: string
  models: AIModelOption[]
  selectedRef: string
  selectedModel: string
  targetHint: string
  targetLabel: string
  providerId?: AIProviderId
  slot?: AIJobSlot
}

export interface AISettingsViewModel {
  assignments: AIModelAssignmentGroup[]
  providerAssignments: AIModelAssignmentGroup[]
  jobAssignments: AIModelAssignmentGroup[]
}

const STORAGE_KEY = 'am2_ai_model_routing'
const CONFIG_STORAGE_KEY = 'am2_ai_config'

const PROVIDER_LABELS: Record<AIProviderId, string> = {
  'vertex-managed': 'Managed Cloud AI',
  gemini: 'Google Gemini',
  openai: 'OpenAI',
  anthropic: 'Anthropic Claude',
  groq: 'Groq',
  ollama: 'Ollama (Local)',
  mock: 'Deterministic Mock',
  'stable-diffusion': 'Stable Diffusion (Local)',
}

const PROVIDER_DESCRIPTIONS: Record<AIProviderId, string> = {
  'vertex-managed': 'Managed Gemini access for hosted workflows and guided drafting.',
  gemini: 'Google Gemini models for prose, reasoning, and image-adjacent planning.',
  openai: 'OpenAI models for general-purpose drafting and coding support.',
  anthropic: 'Claude models for precise prose, reasoning, and long-context editing.',
  groq: 'Fast hosted open-model inference for quick drafting and coding loops.',
  ollama: 'Local models for private, offline, and low-cost generation.',
  mock: 'Local deterministic mock for testing and offline debugging.',
  'stable-diffusion': 'Local image generation via Stable Diffusion (Automatic1111).',
}

const JOB_META: Record<AIJobSlot, { label: string; description: string }> = {
  quality: {
    label: 'Quality',
    description: 'Reasoning-heavy drafting, world synthesis, and final polish.',
  },
  prose: {
    label: 'Just Prose',
    description: 'Narration, commercial copy, lore, and long-form descriptive text.',
  },
  coding: {
    label: 'Coding',
    description: 'JS forging, logic synthesis, validation, and structured transforms.',
  },
  images: {
    label: 'Images',
    description: 'Vision-aware prompt shaping and image-side planning.',
  },
  music: {
    label: 'Music / Audio',
    description: 'Musical sketches, score ideas, and other audio-oriented generation.',
  },
  tts: {
    label: 'TTS',
    description: 'Voice script shaping, pronunciation, pacing, and speech prep.',
  },
}

function providerAssignmentId(providerId: AIProviderId): AISelectionTargetId {
  return `provider:${providerId}` as AISelectionTargetId
}

function jobAssignmentId(slot: AIJobSlot): AISelectionTargetId {
  return `job:${slot}` as AISelectionTargetId
}

function isProviderAssignmentId(id: AISelectionTargetId): id is `provider:${AIProviderId}` {
  return String(id).startsWith('provider:')
}

function defaultAssignmentModel(providerId: AIProviderId, rawConfig: Record<string, unknown>): string {
  if (providerId === 'ollama') {
    return String(rawConfig.ollamaModel ?? rawConfig.ollamaFastModel ?? rawConfig.ollamaQualityModel ?? 'llama3.2').trim() || 'llama3.2'
  }
  return MODEL_LIBRARY[providerId][0]?.model ?? ''
}

const MODEL_LIBRARY: Record<AIProviderId, Array<{ model: string; profile: AIModelProfile; note: string }>> = {
  'vertex-managed': [
    {
      model: 'gemini-2.5-flash',
      profile: { coding: 74, reasoning: 80, creative: 78, vision: 70, speed: 85, instructions: 92 },
      note: 'Balanced managed Gemini model.',
    },
    {
      model: 'gemini-2.0-flash',
      profile: { coding: 70, reasoning: 76, creative: 72, vision: 65, speed: 88, instructions: 88 },
      note: 'Faster managed Gemini fallback.',
    },
    {
      model: 'gemini-2.5-pro',
      profile: { coding: 82, reasoning: 90, creative: 86, vision: 75, speed: 55, instructions: 95 },
      note: 'Highest quality managed Gemini option.',
    },
  ],
  gemini: [
    {
      model: 'gemini-2.5-flash',
      profile: { coding: 74, reasoning: 80, creative: 78, vision: 70, speed: 85, instructions: 92 },
      note: 'Balanced Gemini model.',
    },
    {
      model: 'gemini-2.0-flash',
      profile: { coding: 70, reasoning: 76, creative: 72, vision: 65, speed: 88, instructions: 88 },
      note: 'Fast Gemini fallback.',
    },
    {
      model: 'gemini-2.5-pro',
      profile: { coding: 82, reasoning: 90, creative: 86, vision: 75, speed: 55, instructions: 95 },
      note: 'High-quality Gemini option.',
    },
  ],
  openai: [
    {
      model: 'gpt-4o-mini',
      profile: { coding: 68, reasoning: 72, creative: 66, vision: 55, speed: 80, instructions: 90 },
      note: 'Low-cost general model.',
    },
    {
      model: 'gpt-4o',
      profile: { coding: 78, reasoning: 82, creative: 80, vision: 70, speed: 60, instructions: 88 },
      note: 'Higher quality OpenAI model.',
    },
  ],
  anthropic: [
    {
      model: 'claude-haiku-4-5-20251001',
      profile: { coding: 60, reasoning: 66, creative: 68, vision: 0, speed: 90, instructions: 94 },
      note: 'Fast Claude option.',
    },
    {
      model: 'claude-sonnet-4-6',
      profile: { coding: 82, reasoning: 86, creative: 84, vision: 0, speed: 55, instructions: 96 },
      note: 'Higher quality Claude option.',
    },
  ],
  groq: [
    {
      model: 'llama-3.1-8b-instant',
      profile: { coding: 62, reasoning: 64, creative: 60, vision: 0, speed: 95, instructions: 82 },
      note: 'Fast Groq open model.',
    },
    {
      model: 'llama-3.3-70b-versatile',
      profile: { coding: 84, reasoning: 86, creative: 83, vision: 0, speed: 50, instructions: 88 },
      note: 'Large Groq open model.',
    },
  ],
  ollama: [
    {
      model: 'llama3.2',
      profile: { coding: 35, reasoning: 45, creative: 58, vision: 0, speed: 96, instructions: 72 },
      note: 'Legacy local fallback.',
    },
  ],
  mock: [
    {
      model: 'mock-stable-v1',
      profile: { coding: 5, reasoning: 5, creative: 10, vision: 0, speed: 99, instructions: 95 },
      note: 'Deterministic local mock.',
    },
  ],
  'stable-diffusion': [
    {
      model: 'v1-5-pruned-emaonly',
      profile: { coding: 0, reasoning: 0, creative: 80, vision: 90, speed: 70, instructions: 60 },
      note: 'Standard SD 1.5 model.',
    },
    {
      model: 'sd_xl_base_1.0',
      profile: { coding: 0, reasoning: 0, creative: 90, vision: 95, speed: 40, instructions: 80 },
      note: 'High quality SDXL model.',
    },
  ],
}

const OLLAMA_DOC_CATALOG: Array<{ pattern: RegExp; profile: AIModelProfile; note: string }> = [
  {
    pattern: /qwen2\.5-coder:7b/i,
    profile: { coding: 78, reasoning: 65, creative: 50, vision: 0, speed: 85, instructions: 75 },
    note: 'Coding-first local model.',
  },
  {
    pattern: /deepseek-r1:8b/i,
    profile: { coding: 60, reasoning: 84, creative: 45, vision: 0, speed: 55, instructions: 62 },
    note: 'Reasoning-first local model.',
  },
  {
    pattern: /stheno-v3\.2:8b/i,
    profile: { coding: 35, reasoning: 52, creative: 94, vision: 0, speed: 75, instructions: 72 },
    note: 'Creative prose local model.',
  },
  {
    pattern: /mistral-nemo:12b/i,
    profile: { coding: 68, reasoning: 70, creative: 82, vision: 0, speed: 62, instructions: 78 },
    note: 'Balanced local generalist.',
  },
  {
    pattern: /llama-3\.1:8b/i,
    profile: { coding: 55, reasoning: 62, creative: 72, vision: 0, speed: 78, instructions: 85 },
    note: 'Strong local generalist.',
  },
  {
    pattern: /llama-3\.2-vision:11b/i,
    profile: { coding: 48, reasoning: 58, creative: 68, vision: 85, speed: 65, instructions: 76 },
    note: 'Local vision-capable model.',
  },
  {
    pattern: /hermes3:8b/i,
    profile: { coding: 58, reasoning: 65, creative: 78, vision: 0, speed: 75, instructions: 92 },
    note: 'Instruction-tuned local model.',
  },
  {
    pattern: /phi(?:\s|\(|-)?(?:3\.5|4)/i,
    profile: { coding: 52, reasoning: 60, creative: 40, vision: 0, speed: 98, instructions: 68 },
    note: 'Fast small local model.',
  },
  {
    pattern: /llama-3\.2:3b/i,
    profile: { coding: 35, reasoning: 45, creative: 58, vision: 0, speed: 96, instructions: 72 },
    note: 'Very fast small local model.',
  },
  {
    pattern: /moondream/i,
    profile: { coding: 5, reasoning: 15, creative: 20, vision: 70, speed: 99, instructions: 45 },
    note: 'Vision-first tiny local model.',
  },
]

function safeStorageGet(key: string): string {
  try {
    if (typeof localStorage === 'undefined') return ''
    return localStorage.getItem(key) || ''
  } catch {
    return ''
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

function parseJson<T>(raw: string, fallback: T): T {
  try {
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}
export function modelSizeB(model: string): number {
  const lower = model.toLowerCase()
  // Look for any number followed by 'b' (e.g. 12b, 7b, 3.5b)
  const match = lower.match(/([0-9]+(?:\.[0-9]+)?)b/)
  if (match && match[1]) {
    const val = Number(match[1])
    if (!isNaN(val)) return val
  }
  if (lower.includes('70b') || lower.includes('72b')) return 70
  if (lower.includes('32b')) return 32
  if (lower.includes('13b')) return 13
  if (lower.includes('12b')) return 12
  if (lower.includes('8b')) return 8
  if (lower.includes('7b')) return 7
  if (lower.includes('3b')) return 3
  return 0
}

export function hardwareCapB(hw: string): number {
  if (hw === '16gb') return 70
  if (hw === '8gb') return 13
  if (hw === '4gb') return 7
  return 4
}

function hardwareLabelFor(hwScore: number): string {
  if (hwScore <= -10) return 'too heavy'
  if (hwScore <= 0) return 'near limit'
  if (hwScore <= 10) return 'fits'
  return 'safe fit'
}

function defaultProfileFor(model: string): AIModelProfile {
  const match = OLLAMA_DOC_CATALOG.find(entry => entry.pattern.test(model))
  if (match) return match.profile
  const size = modelSizeB(model)
  if (size >= 32) return { coding: 72, reasoning: 78, creative: 70, vision: 0, speed: 40, instructions: 85 }
  if (size >= 13) return { coding: 66, reasoning: 68, creative: 66, vision: 0, speed: 58, instructions: 82 }
  if (size >= 8) return { coding: 58, reasoning: 60, creative: 60, vision: 0, speed: 78, instructions: 78 }
  if (size > 0) return { coding: 42, reasoning: 48, creative: 50, vision: 0, speed: 92, instructions: 70 }
  return { coding: 50, reasoning: 55, creative: 50, vision: 0, speed: 70, instructions: 75 }
}

function modelProfileFor(providerId: AIProviderId, model: string, hardware = 'cpu'): { profile: AIModelProfile; note: string; hardwareScore: number; hardwareLabel: string } {
  if (providerId === 'ollama') {
    const size = modelSizeB(model)
    const cap = hardwareCapB(hardware)
    const hwScore = cap - size
    const docMatch = OLLAMA_DOC_CATALOG.find(entry => entry.pattern.test(model))
    const profile = docMatch?.profile ?? defaultProfileFor(model)
    return {
      profile,
      note: docMatch?.note ?? 'Local model.',
      hardwareScore: hwScore,
      hardwareLabel: hardwareLabelFor(hwScore),
    }
  }
  const preset = MODEL_LIBRARY[providerId].find(entry => entry.model === model)
  const profile = preset?.profile ?? defaultProfileFor(model)
  return {
    profile,
    note: preset?.note ?? 'Configured model.',
    hardwareScore: 0,
    hardwareLabel: 'n/a',
  }
}

function costTierFor(providerId: AIProviderId, model: string, profile: AIModelProfile): 0 | 1 | 2 | 3 {
  if (providerId === 'ollama') return 0
  const lower = model.toLowerCase()
  if (lower.includes('mini') || lower.includes('flash') || lower.includes('haiku') || lower.includes('instant')) return 1
  if (lower.includes('pro') || lower.includes('sonnet') || lower.includes('70b')) return 3
  if (profile.reasoning >= 86 || profile.coding >= 82 || profile.creative >= 84) return 3
  if (profile.reasoning >= 76 || profile.coding >= 74 || profile.creative >= 76) return 2
  return 1
}

function weightForSection(section: AIJobSlot): Record<keyof AIModelProfile, number> {
  switch (section) {
    case 'quality':
      return { coding: 0.16, reasoning: 0.34, creative: 0.18, vision: 0.06, speed: 0.08, instructions: 0.18 }
    case 'prose':
      return { coding: 0.05, reasoning: 0.2, creative: 0.34, vision: 0.08, speed: 0.08, instructions: 0.25 }
    case 'coding':
      return { coding: 0.4, reasoning: 0.26, creative: 0.04, vision: 0.0, speed: 0.16, instructions: 0.14 }
    case 'images':
      return { coding: 0.02, reasoning: 0.08, creative: 0.22, vision: 0.48, speed: 0.1, instructions: 0.1 }
    case 'music':
      return { coding: 0.04, reasoning: 0.08, creative: 0.46, vision: 0.0, speed: 0.22, instructions: 0.2 }
    case 'tts':
      return { coding: 0.02, reasoning: 0.1, creative: 0.18, vision: 0.02, speed: 0.34, instructions: 0.34 }
  }
}

function scoreOption(section: AIJobSlot, option: Omit<AIModelOption, 'score' | 'costTier' | 'costLabel' | 'selected'> & { costTier: 0 | 1 | 2 | 3; hardwareScore: number }): number {
  const weights = weightForSection(section)
  const p = option.profile
  const lower = option.model.toLowerCase()
  const raw = (
    (p.coding * weights.coding)
    + (p.reasoning * weights.reasoning)
    + (p.creative * weights.creative)
    + (p.vision * weights.vision)
    + (p.speed * weights.speed)
    + (p.instructions * weights.instructions)
  )
  const costPenalty = option.costTier * (section === 'coding' ? 4 : section === 'prose' ? 3 : section === 'music' ? 2 : 2.5)
  const hardwarePenalty = option.hardwareScore < 0 ? Math.min(16, Math.abs(option.hardwareScore) * 1.25) : 0
  let specialistBoost = 0
  if (section === 'coding') {
    if (lower.includes('coder')) specialistBoost += 14
    if (lower.includes('qwen')) specialistBoost += 8
    if (lower.includes('deepseek')) specialistBoost += 6
    if (lower.includes('llama-3.3') || lower.includes('sonnet') || lower.includes('gpt-4o')) specialistBoost += 2
  } else if (section === 'images') {
    if (p.vision >= 70) specialistBoost += 12
    if (lower.includes('vision')) specialistBoost += 8
    if (lower.includes('moondream')) specialistBoost += 14
  } else if (section === 'prose') {
    if (lower.includes('sonnet')) specialistBoost += 10
    if (lower.includes('hermes')) specialistBoost += 6
    if (lower.includes('stheno')) specialistBoost += 10
  } else if (section === 'music') {
    if (lower.includes('instant') || lower.includes('flash')) specialistBoost += 4
  } else if (section === 'tts') {
    if (lower.includes('mini') || lower.includes('flash') || lower.includes('haiku')) specialistBoost += 5
  } else {
    if (lower.includes('sonnet') || lower.includes('gpt-4o') || lower.includes('gemini-2.5')) specialistBoost += 4
  }
  return Math.round(raw + specialistBoost - costPenalty - hardwarePenalty)
}

function costLabelFor(costTier: 0 | 1 | 2 | 3): '0' | '+' | '++' | '+++' {
  if (costTier === 0) return '0'
  if (costTier === 1) return '+'
  if (costTier === 2) return '++'
  return '+++'
}

function buildOption(providerId: AIProviderId, model: string, section: AIJobSlot, selectedRef: string, configHardware = 'cpu'): AIModelOption {
  const providerLabel = PROVIDER_LABELS[providerId]
  const { profile, note, hardwareScore, hardwareLabel } = modelProfileFor(providerId, model, configHardware)
  const costTier = costTierFor(providerId, model, profile)
  const option: AIModelOption = {
    ref: encodeModelRef(providerId, model),
    providerId,
    providerLabel,
    model,
    label: `${providerLabel} · ${model}`,
    profile,
    score: 0,
    costTier,
    costLabel: costLabelFor(costTier),
    hardwareLabel,
    hardwareScore,
    note,
    available: true,
    selected: false,
  }
  option.score = scoreOption(section, option)
  option.selected = option.ref === selectedRef
  return option
}

function buildOptionsForProvider(providerId: AIProviderId, models: string[], section: AIJobSlot, selectedRef: string, configHardware = 'cpu'): AIModelOption[] {
  return models.map(model => buildOption(providerId, model, section, selectedRef, configHardware))
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
}

function providerIsEnabled(rawConfig: Record<string, unknown>, providerId: AIProviderId): boolean {
  if (providerId === 'vertex-managed' || providerId === 'mock') return true
  switch (providerId) {
    case 'gemini':
      return !!rawConfig.geminiEnabled && (!!rawConfig.geminiApiKey || !!rawConfig.geminiProxyUrl)
    case 'openai':
      return !!rawConfig.openaiEnabled && !!rawConfig.openaiApiKey
    case 'anthropic':
      return !!rawConfig.anthropicEnabled && !!rawConfig.anthropicApiKey
    case 'groq':
      return !!rawConfig.groqEnabled && !!rawConfig.groqApiKey
    case 'ollama':
      return !!rawConfig.ollamaEnabled
    case 'stable-diffusion':
      return !!rawConfig.stableDiffusionEnabled
  }
}

function rawConfigFromStorage(): Record<string, unknown> {
  const raw = safeStorageGet(CONFIG_STORAGE_KEY)
  return parseJson<Record<string, unknown>>(raw, {})
}

function defaultRoutingFromConfig(rawConfig: Record<string, unknown>): AIModelRoutingState {
  return {
    assignments: {
      [providerAssignmentId('vertex-managed')]: encodeModelRef('vertex-managed', defaultAssignmentModel('vertex-managed', rawConfig)),
      [providerAssignmentId('gemini')]: encodeModelRef('gemini', defaultAssignmentModel('gemini', rawConfig)),
      [providerAssignmentId('openai')]: encodeModelRef('openai', defaultAssignmentModel('openai', rawConfig)),
      [providerAssignmentId('anthropic')]: encodeModelRef('anthropic', defaultAssignmentModel('anthropic', rawConfig)),
      [providerAssignmentId('groq')]: encodeModelRef('groq', defaultAssignmentModel('groq', rawConfig)),
      [providerAssignmentId('ollama')]: encodeModelRef('ollama', defaultAssignmentModel('ollama', rawConfig)),
      [jobAssignmentId('quality')]: 'gemini:::gemini-2.5-flash',
      [jobAssignmentId('prose')]: 'anthropic:::claude-sonnet-4-6',
      [jobAssignmentId('coding')]: `ollama:::${defaultAssignmentModel('ollama', rawConfig)}`,
      [jobAssignmentId('images')]: 'gemini:::gemini-2.5-flash',
      [jobAssignmentId('music')]: 'groq:::llama-3.1-8b-instant',
      [jobAssignmentId('tts')]: 'openai:::gpt-4o-mini',
    } as Record<AISelectionTargetId, string>,
  }
}

export function encodeModelRef(providerId: AIProviderId, model: string): string {
  return `${providerId}:::${model}`
}

export function decodeModelRef(raw: string): { providerId: AIProviderId; model: string } | null {
  const value = String(raw ?? '').trim()
  const marker = ':::'
  const idx = value.indexOf(marker)
  if (idx <= 0) return null
  const providerId = value.slice(0, idx) as AIProviderId
  const model = value.slice(idx + marker.length).trim()
  if (!model) return null
  if (!(providerId in PROVIDER_LABELS)) return null
  return { providerId, model }
}

export function loadAIModelRoutingState(): AIModelRoutingState {
  const rawConfig = rawConfigFromStorage()
  const defaults = defaultRoutingFromConfig(rawConfig)
  const stored = parseJson<Partial<AIModelRoutingState> & { providerDefaults?: Record<AIProviderId, string>; jobAssignments?: Record<AIJobSlot, string> }>(safeStorageGet(STORAGE_KEY), {})
  const merged: AIModelRoutingState = {
    assignments: {
      ...defaults.assignments,
      ...(stored.assignments ?? {}),
    } as Record<AISelectionTargetId, string>,
  }
  if (stored.providerDefaults) {
    for (const [providerId, model] of Object.entries(stored.providerDefaults) as Array<[AIProviderId, string]>) {
      merged.assignments[providerAssignmentId(providerId)] = encodeModelRef(providerId, model)
    }
  }
  if (stored.jobAssignments) {
    for (const [slot, ref] of Object.entries(stored.jobAssignments) as Array<[AIJobSlot, string]>) {
      merged.assignments[jobAssignmentId(slot)] = ref
    }
  }
  return normalizeAIModelRoutingState(merged, rawConfig)
}

export function saveAIModelRoutingState(state: AIModelRoutingState): void {
  const normalized = normalizeAIModelRoutingState(state, rawConfigFromStorage())
  safeStorageSet(STORAGE_KEY, JSON.stringify(normalized))
}

export function normalizeAIModelRoutingState(state: AIModelRoutingState, rawConfig: Record<string, unknown> = rawConfigFromStorage()): AIModelRoutingState {
  const defaults = defaultRoutingFromConfig(rawConfig)
  const next: AIModelRoutingState = {
    assignments: {
      ...defaults.assignments,
      ...(state.assignments ?? {}),
    } as Record<AISelectionTargetId, string>,
  }

  for (const [targetId, rawRef] of Object.entries(next.assignments) as Array<[AISelectionTargetId, string]>) {
    const decoded = decodeModelRef(rawRef)
    if (!decoded) {
      next.assignments[targetId] = defaults.assignments[targetId]
      continue
    }
    if (isProviderAssignmentId(targetId) && targetId !== providerAssignmentId(decoded.providerId)) {
      next.assignments[targetId] = defaults.assignments[targetId]
      continue
    }
    next.assignments[targetId] = encodeModelRef(decoded.providerId, decoded.model)
  }

  for (const providerId of Object.keys(PROVIDER_LABELS) as AIProviderId[]) {
    const providerKey = providerAssignmentId(providerId)
    if (!next.assignments[providerKey]) {
      next.assignments[providerKey] = defaults.assignments[providerKey]
    }
  }
  return next
}

export function resolveAIModelOverride(jobType: string): { providerId: AIProviderId; model: string } | null {
  const mappedSlot = jobTypeToSlot(jobType)
  if (!mappedSlot) return null
  const routing = loadAIModelRoutingState()
  const ref = routing.assignments[jobAssignmentId(mappedSlot)]
  const decoded = decodeModelRef(ref)
  if (!decoded) return null
  const rawConfig = rawConfigFromStorage()
  if (!providerIsEnabled(rawConfig, decoded.providerId)) return null
  return decoded
}

export function jobTypeToSlot(jobType: string): AIJobSlot | null {
  switch (jobType) {
    case 'genesis':
    case 'world_forge':
    case 'world_enrich':
    case 'preforge_draft':
      return 'quality'
    case 'narrator':
      return 'prose'
    case 'logic_forge':
    case 'action_validator':
    case 'trait_shift':
    case 'world_tick':
    case 'cheat':
      return 'coding'
    case 'music_forge':
      return 'music'
    case 'image':
      return 'images'
    case 'tts':
      return 'tts'
    default:
      return null
  }
}

function buildProviderModels(providerId: AIProviderId, enabled: boolean, config: Record<string, unknown>, routing: AIModelRoutingState, ollamaModels: string[]): AIModelOption[] {
  const hardware = String(config.ollamaHardware ?? 'cpu')
  const selected = decodeModelRef(routing.assignments[providerAssignmentId(providerId)])?.model || ''
  const library = providerId === 'ollama'
    ? (ollamaModels.length ? ollamaModels : [String(config.ollamaModel ?? 'llama3.2')])
    : MODEL_LIBRARY[providerId].map(entry => entry.model)
  const section = providerId === 'ollama' ? 'coding' : 'quality'
  const options = buildOptionsForProvider(providerId, library, section, encodeModelRef(providerId, selected || library[0] || ''), hardware)
  return enabled ? options : options.map(option => ({ ...option, available: false }))
}

function buildSectionModels(section: AIJobSlot, config: Record<string, unknown>, routing: AIModelRoutingState, ollamaModels: string[]): AIModelOption[] {
  const providerSets: Array<AIProviderId> = ['vertex-managed', 'gemini', 'openai', 'anthropic', 'groq', 'ollama', 'mock', 'stable-diffusion']
  const models: AIModelOption[] = []
  const hardware = String(config.ollamaHardware ?? 'cpu')
  for (const providerId of providerSets) {
    const providerEnabled = providerIsEnabled(config, providerId)
    const library = providerId === 'ollama'
      ? (ollamaModels.length ? ollamaModels : [String(config.ollamaModel ?? 'llama3.2')])
      : MODEL_LIBRARY[providerId].map(entry => entry.model)
    const providerSelectedRef = routing.assignments[providerAssignmentId(providerId)] || encodeModelRef(providerId, library[0] || '')
    for (const model of library) {
      const option = buildOption(providerId, model, section, routing.assignments[jobAssignmentId(section)] || providerSelectedRef, hardware)
      models.push({
        ...option,
        available: providerEnabled,
      })
    }
  }
  models.sort((a, b) => b.score - a.score || a.providerLabel.localeCompare(b.providerLabel) || a.model.localeCompare(b.model))
  return models.map(option => ({
    ...option,
    selected: option.ref === routing.assignments[jobAssignmentId(section)],
  }))
}

export function buildAISettingsViewModel(
  config: Record<string, unknown>,
  routing: AIModelRoutingState,
  ollamaModels: string[] = [],
): AISettingsViewModel {
  const normalized = normalizeAIModelRoutingState(routing, config)
  const providerAssignments: AIModelAssignmentGroup[] = [
    {
      kind: 'provider',
      id: providerAssignmentId('vertex-managed'),
      providerId: 'vertex-managed',
      label: PROVIDER_LABELS['vertex-managed'],
      enabled: true,
      canToggle: false,
      desc: PROVIDER_DESCRIPTIONS['vertex-managed'],
      models: buildProviderModels('vertex-managed', true, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('vertex-managed')] || encodeModelRef('vertex-managed', defaultAssignmentModel('vertex-managed', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('vertex-managed')])?.model || defaultAssignmentModel('vertex-managed', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS['vertex-managed'],
    },
    {
      kind: 'provider',
      id: providerAssignmentId('gemini'),
      providerId: 'gemini',
      label: PROVIDER_LABELS.gemini,
      enabled: !!config.geminiEnabled,
      canToggle: true,
      keyField: 'geminiApiKey',
      proxyField: 'geminiProxyUrl',
      toggleField: 'geminiEnabled',
      desc: PROVIDER_DESCRIPTIONS.gemini,
      models: buildProviderModels('gemini', !!config.geminiEnabled, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('gemini')] || encodeModelRef('gemini', defaultAssignmentModel('gemini', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('gemini')])?.model || defaultAssignmentModel('gemini', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS.gemini,
    },
    {
      kind: 'provider',
      id: providerAssignmentId('openai'),
      providerId: 'openai',
      label: PROVIDER_LABELS.openai,
      enabled: !!config.openaiEnabled,
      canToggle: true,
      keyField: 'openaiApiKey',
      toggleField: 'openaiEnabled',
      desc: PROVIDER_DESCRIPTIONS.openai,
      models: buildProviderModels('openai', !!config.openaiEnabled, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('openai')] || encodeModelRef('openai', defaultAssignmentModel('openai', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('openai')])?.model || defaultAssignmentModel('openai', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS.openai,
    },
    {
      kind: 'provider',
      id: providerAssignmentId('anthropic'),
      providerId: 'anthropic',
      label: PROVIDER_LABELS.anthropic,
      enabled: !!config.anthropicEnabled,
      canToggle: true,
      keyField: 'anthropicApiKey',
      toggleField: 'anthropicEnabled',
      desc: PROVIDER_DESCRIPTIONS.anthropic,
      models: buildProviderModels('anthropic', !!config.anthropicEnabled, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('anthropic')] || encodeModelRef('anthropic', defaultAssignmentModel('anthropic', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('anthropic')])?.model || defaultAssignmentModel('anthropic', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS.anthropic,
    },
    {
      kind: 'provider',
      id: providerAssignmentId('groq'),
      providerId: 'groq',
      label: PROVIDER_LABELS.groq,
      enabled: !!config.groqEnabled,
      canToggle: true,
      keyField: 'groqApiKey',
      toggleField: 'groqEnabled',
      desc: PROVIDER_DESCRIPTIONS.groq,
      models: buildProviderModels('groq', !!config.groqEnabled, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('groq')] || encodeModelRef('groq', defaultAssignmentModel('groq', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('groq')])?.model || defaultAssignmentModel('groq', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS.groq,
    },
    {
      kind: 'provider',
      id: providerAssignmentId('ollama'),
      providerId: 'ollama',
      label: PROVIDER_LABELS.ollama,
      enabled: !!config.ollamaEnabled,
      canToggle: true,
      baseUrlField: 'ollamaBaseUrl',
      hardwareField: 'ollamaHardware',
      toggleField: 'ollamaEnabled',
      desc: PROVIDER_DESCRIPTIONS.ollama,
      models: buildProviderModels('ollama', !!config.ollamaEnabled, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('ollama')] || encodeModelRef('ollama', defaultAssignmentModel('ollama', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('ollama')])?.model || defaultAssignmentModel('ollama', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS.ollama,
    },
    {
      kind: 'provider',
      id: providerAssignmentId('mock'),
      providerId: 'mock',
      label: PROVIDER_LABELS.mock,
      enabled: true,
      canToggle: false,
      desc: PROVIDER_DESCRIPTIONS.mock,
      models: buildProviderModels('mock', true, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('mock')] || encodeModelRef('mock', defaultAssignmentModel('mock', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('mock')])?.model || defaultAssignmentModel('mock', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS.mock,
    },
    {
      kind: 'provider',
      id: providerAssignmentId('stable-diffusion'),
      providerId: 'stable-diffusion',
      label: PROVIDER_LABELS['stable-diffusion'],
      enabled: !!config.stableDiffusionEnabled,
      canToggle: true,
      baseUrlField: 'stableDiffusionBaseUrl',
      toggleField: 'stableDiffusionEnabled',
      desc: PROVIDER_DESCRIPTIONS['stable-diffusion'],
      models: buildProviderModels('stable-diffusion', !!config.stableDiffusionEnabled, config, normalized, ollamaModels),
      selectedRef: normalized.assignments[providerAssignmentId('stable-diffusion')] || encodeModelRef('stable-diffusion', defaultAssignmentModel('stable-diffusion', config)),
      selectedModel: decodeModelRef(normalized.assignments[providerAssignmentId('stable-diffusion')])?.model || defaultAssignmentModel('stable-diffusion', config),
      targetHint: 'Provider model',
      targetLabel: PROVIDER_LABELS['stable-diffusion'],
    },
  ]
  const jobAssignments: AIModelAssignmentGroup[] = []
  for (const slot of Object.keys(JOB_META) as AIJobSlot[]) {
    jobAssignments.push({
      kind: 'job',
      id: jobAssignmentId(slot),
      slot,
      label: JOB_META[slot].label,
      enabled: true,
      canToggle: false,
      desc: JOB_META[slot].description,
      models: buildSectionModels(slot, config, normalized, ollamaModels).map(option => ({
        ...option,
        available: option.available && (option.providerId !== 'ollama' || !!config.ollamaEnabled),
      })),
      selectedRef: normalized.assignments[jobAssignmentId(slot)],
      selectedModel: decodeModelRef(normalized.assignments[jobAssignmentId(slot)])?.model ?? '',
      targetHint: 'Job allocation',
      targetLabel: JOB_META[slot].label,
    })
  }

  return {
    assignments: [...providerAssignments, ...jobAssignments],
    providerAssignments,
    jobAssignments,
  }
}

export function formatAIModelBadge(option: Pick<AIModelOption, 'score' | 'costLabel' | 'hardwareLabel' | 'providerId'>): string {
  const hardware = option.providerId === 'ollama' ? ` · ${option.hardwareLabel}` : ''
  return `${option.score}/100 · ${option.costLabel}${hardware}`
}

export function suggestedModelText(option: AIModelOption): string {
  const parts = [
    `${option.score}/100`,
    `cost ${option.costLabel}`,
  ]
  if (option.providerId === 'ollama') parts.push(`hw ${option.hardwareLabel}`)
  return parts.join(' · ')
}
